import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

const emphasisNodes = new Set(["StrongEmphasis", "Emphasis"]);

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

/**
 * Build decorations for bold (`**`) and italic (`*`) emphasis.
 *
 * When the cursor is NOT on the element's line, the `EmphasisMark` tokens
 * (the `*` / `**` markers) are hidden with replace decorations. The text
 * content between markers keeps its bold/italic styling from the syntax
 * highlighting theme.
 *
 * Emphasis nodes that are inside heading nodes are skipped to avoid
 * overlapping replace decorations.
 */
export function buildEmphasisDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const cursorLine = cursor.cursorLine;

  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      if (!emphasisNodes.has(node.name)) return;

      const nodeLine = state.doc.lineAt(node.from).number;

      // If cursor is on this line, show raw markdown
      if (cursorLine === nodeLine) return;

      // Skip emphasis inside headings to prevent overlapping decorations.
      // Walk up the tree to check for heading ancestors.
      let ancestor = node.node.parent;
      while (ancestor) {
        if (ancestor.name.startsWith("ATXHeading")) return;
        ancestor = ancestor.parent;
      }

      // Find EmphasisMark children and hide them
      const childCursor = node.node.cursor();
      if (childCursor.firstChild()) {
        do {
          if (childCursor.name === "EmphasisMark") {
            decorations.push({
              from: childCursor.from,
              to: childCursor.to,
              decoration: Decoration.replace({}),
            });
          }
        } while (childCursor.nextSibling());
      }
    },
  });
}
